import { useEffect, useState } from "react";
import api, { eur, fmtDate, formatApiError } from "@/lib/apiClient";
import AdminLayout from "@/layouts/AdminLayout";
import { adminNav } from "@/lib/navConfig";
import { PageHeader } from "@/components/Common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Trash2, ReceiptText, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import { ExpenseDetail } from "@/components/ExpenseDetail";

const DRow = ({ label, children }) => (
  <div className="flex justify-between gap-4 py-1.5 border-b border-zinc-100 text-sm"><span className="text-zinc-500">{label}</span><span className="text-right font-medium">{children ?? "—"}</span></div>
);
const BILLING = { non_billable: "Non-billable", at_cost: "At cost", markup: "Mark-up", manual: "Manual price" };

function CostDetailDialog({ m, onClose, incVat }) {
  return (
    <Dialog open={!!m} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto kqm-scroll" data-testid="material-detail-dialog">
        <DialogHeader><DialogTitle>{m?.source === "expense" ? "Employee expense" : "Cost details"}</DialogTitle></DialogHeader>
        {m && <>
          <DRow label="Date">{fmtDate(m.date)}</DRow>
          <DRow label="Type">{m.cost_type === "supplier_costs" ? "Supplier Costs" : "Materials"}</DRow>
          <DRow label="Site">{m.site_name}</DRow>
          <DRow label="Category">{m.category}</DRow>
          <DRow label="Supplier">{m.supplier}</DRow>
          <DRow label="Description">{m.description}</DRow>
          <DRow label="Quantity">{m.quantity}</DRow>
          <DRow label="Unit cost (ex VAT)">{eur(m.unit_cost)}</DRow>
          <DRow label="Cost ex VAT">{eur(m.total_cost)}</DRow>
          <DRow label={`VAT (${m.vat_rate ?? 23}%)`}>{eur(incVat(m) - Number(m.total_cost || 0))}</DRow>
          <DRow label="Cost inc VAT">{eur(incVat(m))}</DRow>
          <DRow label="Billing">{BILLING[m.billing] || m.billing}</DRow>
          <DRow label="Sale price">{eur(m.sale_price)}</DRow>
          <DRow label="Gross profit">{eur(m.gross_profit)}</DRow>
          {m.invoice_ref && <DRow label="Invoice ref">{m.invoice_ref}</DRow>}
          <DRow label="Entered by">{m.entered_by}</DRow>
          {m.expense_id && <div className="mt-4"><div className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-1">Expense claim</div><ExpenseDetail id={m.expense_id} /></div>}
        </>}
      </DialogContent>
    </Dialog>
  );
}

const OTHER = "__other__";
const NONE = "__none__";
const EMPTY = { cost_type: "materials", vat_rate: 23, site_id: "", category: "", supplier: "", supplierMode: "", description: "", quantity: 1, unit_cost: 0, billing: "markup", markup_pct: 20, sale_price: "" };
const TYPE_LABEL = { materials: "Materials", supplier_costs: "Supplier Costs" };
const typeOf = (m) => m.cost_type || "materials";
const EMPTY_CN = { site_id: "", material_id: "", reason: "", supplier: "", supplierMode: "", supplier_credit: "", customer_id: "", customer_credit: "" };

export default function Materials() {
  const [rows, setRows] = useState([]);
  const [credits, setCredits] = useState([]);
  const [cats, setCats] = useState([]);
  const [sites, setSites] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [create, setCreate] = useState(false);
  const [cnOpen, setCnOpen] = useState(false);
  const [siteFilter, setSiteFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [supFilter, setSupFilter] = useState("all");
  const [viewing, setViewing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [cn, setCn] = useState(EMPTY_CN);
  const [spendOpen, setSpendOpen] = useState(false);
  const [spend, setSpend] = useState([]);

  const loadSuppliers = () => api.get("/suppliers").then((r) => setSuppliers(r.data)).catch(() => {});
  const load = () => {
    const p = siteFilter !== "all" ? { site_id: siteFilter } : {};
    api.get("/materials", { params: p }).then((r) => setRows(r.data));
    api.get("/credit-notes", { params: p }).then((r) => setCredits(r.data)).catch(() => {});
  };
  useEffect(() => {
    api.get("/sites").then((r) => setSites(r.data));
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
    loadSuppliers();
    api.get("/cost-categories").then((r) => {
      const enabled = r.data.filter((c) => c.enabled !== false).map((c) => c.name);
      setCats(enabled);
      setForm((f) => ({ ...f, category: f.category || enabled[0] || "" }));
    });
  }, []);
  useEffect(() => { load(); }, [siteFilter]);

  const siteCustomer = (sid) => sites.find((s) => s.id === sid)?.customer_id || "";
  const addSupplier = async (name) => { try { await api.post("/suppliers", { name }); loadSuppliers(); } catch { /* ignore */ } };

  const openCreate = () => { setForm({ ...EMPTY, category: cats[0] || "", site_id: sites[0]?.id || "" }); setCreate(true); };
  const submit = async () => {
    if (!form.site_id) { toast.error("Select a site"); return; }
    const supplier = form.supplierMode === OTHER ? form.supplier.trim() : form.supplierMode;
    if (form.supplierMode === OTHER && !supplier) { toast.error("Enter the supplier name"); return; }
    const payload = {
      cost_type: form.cost_type, vat_rate: Number(form.vat_rate),
      site_id: form.site_id, category: form.category, supplier, description: form.description,
      billing: form.billing, quantity: Number(form.quantity), unit_cost: Number(form.unit_cost),
      markup_pct: Number(form.markup_pct),
    };
    if (form.billing === "manual") payload.sale_price = Number(form.sale_price);
    try {
      if (form.supplierMode === OTHER && supplier) await addSupplier(supplier);
      await api.post("/materials", payload); toast.success("Cost added to site"); setCreate(false); load(); loadSuppliers();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openCn = (material) => {
    const sid = material?.site_id || (siteFilter !== "all" ? siteFilter : sites[0]?.id || "");
    setCn({ ...EMPTY_CN, site_id: sid, material_id: material?.id || "", customer_id: siteCustomer(sid) });
    setCnOpen(true);
  };
  const submitCn = async () => {
    const supplier = cn.supplierMode === OTHER ? cn.supplier.trim() : cn.supplierMode;
    const supCred = Number(cn.supplier_credit) || 0;
    const custCred = Number(cn.customer_credit) || 0;
    if (!cn.site_id) { toast.error("Select a site"); return; }
    if (!cn.reason.trim()) { toast.error("Enter a reason"); return; }
    if (!supCred && !custCred) { toast.error("Enter a supplier and/or customer credit"); return; }
    if (supCred > 0 && !supplier) { toast.error("Select a supplier for the supplier credit"); return; }
    if (supplier && !supCred) { toast.error("Enter a supplier credit amount"); return; }
    if (custCred > 0 && !cn.customer_id) { toast.error("Select a customer for the customer credit"); return; }
    if (cn.customer_id && !custCred) { toast.error("Enter a customer credit amount"); return; }
    try {
      if (cn.supplierMode === OTHER && supplier) await addSupplier(supplier);
      await api.post("/credit-notes", { site_id: cn.site_id, material_id: cn.material_id || null, reason: cn.reason, supplier, customer_id: cn.customer_id || null, supplier_credit: supCred, customer_credit: custCred });
      toast.success("Credit note raised"); setCnOpen(false); load(); loadSuppliers();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const delCn = async (id) => { if (!window.confirm("Delete this credit note?")) return; try { await api.delete(`/credit-notes/${id}`); toast.success("Credit note deleted"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const openSpend = () => { api.get("/reports/supplier-spend").then((r) => setSpend(r.data)).catch(() => setSpend([])); setSpendOpen(true); };

  const cnSiteMaterials = rows.filter((m) => m.site_id === cn.site_id);
  const byType = typeFilter === "all" ? rows : typeFilter === "credit_note" ? [] : rows.filter((m) => typeOf(m) === typeFilter);
  const shownRows = supFilter === "all" ? byType : byType.filter((m) => (m.supplier || "") === supFilter);
  const shownCredits = supFilter === "all" ? credits : credits.filter((c) => (c.supplier || "") === supFilter);
  const exVat = (m) => Number(m.total_cost) || 0;
  const incVat = (m) => m.total_inc_vat != null ? Number(m.total_inc_vat) : exVat(m) * 1.23;
  const showCredits = typeFilter === "all" || typeFilter === "credit_note";
  const sum = (arr, k) => arr.reduce((a, x) => a + (Number(x[k]) || 0), 0);
  const totals = { cost: sum(shownRows, "total_cost"), inc: shownRows.reduce((a, m) => a + incVat(m), 0), sale: sum(shownRows, "sale_price"), gp: sum(shownRows, "gross_profit"), supCred: showCredits ? sum(shownCredits, "supplier_credit") : 0, custCred: showCredits ? sum(shownCredits, "customer_credit") : 0 };
  const delMat = async (m) => { if (!window.confirm("Delete this cost entry?")) return; try { await api.delete(`/materials/${m.id}`); toast.success("Entry deleted"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const relabel = async (m, t) => { try { await api.put(`/materials/${m.id}`, { cost_type: t }); toast.success(`Moved to ${TYPE_LABEL[t]}`); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  return (
    <AdminLayout nav={adminNav} title="Materials and Costs">
      <PageHeader title="Materials and Costs" subtitle={`${rows.length} entries · ${credits.length} credit note${credits.length === 1 ? "" : "s"}`}>
        <div className="flex flex-wrap gap-2">
          <Button data-testid="supplier-spend-btn" variant="outline" onClick={openSpend}><BarChart3 className="h-4 w-4 mr-1" />Supplier spend</Button>
          <Button data-testid="new-credit-note-btn" variant="outline" onClick={() => openCn(null)}>+ Credit note</Button>
          <Button data-testid="new-material-btn" onClick={openCreate} className="bg-slate-900 text-white">+ Add cost</Button>
        </div>
      </PageHeader>
      <div className="flex flex-wrap gap-2 mb-4">
        <Select value={siteFilter} onValueChange={setSiteFilter}>
          <SelectTrigger data-testid="material-site-filter" className="w-60"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All sites</SelectItem>{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger data-testid="material-type-filter" className="w-48"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Type: All</SelectItem>
            <SelectItem value="materials">Materials</SelectItem>
            <SelectItem value="credit_note">Credit Note</SelectItem>
            <SelectItem value="supplier_costs">Supplier Costs</SelectItem>
          </SelectContent>
        </Select>
        <Select value={supFilter} onValueChange={setSupFilter}>
          <SelectTrigger data-testid="material-supplier-filter" className="w-56"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">All suppliers</SelectItem>{suppliers.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-4" data-testid="material-totals">
        {[["Cost ex VAT", totals.cost, ""], ["Cost inc VAT", totals.inc, ""], ["Sale", totals.sale, ""], ["Gross profit", totals.gp, "text-emerald-600"], ["Supplier credits", totals.supCred, "text-emerald-600"], ["Customer credits", totals.custCred, "text-rose-600"]].map(([l, v, t]) => (
          <div key={l} className="bg-white border border-zinc-200 rounded-md px-3 py-2"><div className="text-[10px] font-bold uppercase tracking-wider text-zinc-500">{l}</div><div className={`num font-bold text-lg ${t}`} data-testid={`total-${l.toLowerCase().replace(/ /g, "-")}`}>{eur(v)}</div></div>
        ))}
      </div>
      {typeFilter !== "credit_note" && <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto kqm-scroll">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-xs font-semibold uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
            <th className="p-3">Date</th><th className="p-3">Type</th><th className="p-3">Site</th><th className="p-3">Category</th><th className="p-3">Supplier</th><th className="p-3">Description</th><th className="p-3 text-right">Cost ex VAT</th><th className="p-3 text-right">VAT</th><th className="p-3 text-right">Inc VAT</th><th className="p-3 text-right">Sale</th><th className="p-3 text-right">GP</th><th className="p-3"></th>
          </tr></thead>
          <tbody>
            {shownRows.length === 0 && <tr><td colSpan={12} className="p-6 text-center text-sm text-zinc-400" data-testid="materials-empty">No costs recorded for this filter.</td></tr>}
            {shownRows.map((m) => (
              <tr key={m.id} data-testid={`material-row-${m.id}`} onClick={() => setViewing(m)} className="border-b border-zinc-50 hover:bg-zinc-50 cursor-pointer">
                <td className="p-3 num">{fmtDate(m.date)}</td>
                <td className="p-3" onClick={(ev) => ev.stopPropagation()}>
                  <Select value={typeOf(m)} onValueChange={(v) => relabel(m, v)}>
                    <SelectTrigger data-testid={`material-type-${m.id}`} className={`h-7 w-36 text-xs ${typeOf(m) === "supplier_costs" ? "bg-sky-50 border-sky-200" : "bg-amber-50 border-amber-200"}`}><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="materials">Materials</SelectItem><SelectItem value="supplier_costs">Supplier Costs</SelectItem></SelectContent>
                  </Select>
                </td>
                <td className="p-3">{m.site_name}</td>
                <td className="p-3 text-zinc-500">{m.category}</td>
                <td className="p-3 text-zinc-500">{m.supplier || "—"}</td>
                <td className="p-3">{m.description}{m.source === "expense" && <span className="ml-1 text-[10px] bg-violet-50 text-violet-700 px-1.5 py-0.5 rounded">Expense</span>}</td>
                <td className="p-3 num text-right">{eur(m.total_cost)}</td>
                <td className="p-3 num text-right text-zinc-500">{m.vat_rate ?? 23}%</td>
                <td className="p-3 num text-right">{eur(incVat(m))}</td>
                <td className="p-3 num text-right">{eur(m.sale_price)}</td>
                <td className="p-3 num text-right whitespace-nowrap text-emerald-600">{eur(m.gross_profit)}</td>
                <td className="p-3 text-right whitespace-nowrap" onClick={(ev) => ev.stopPropagation()}><Button data-testid={`material-view-${m.id}`} size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setViewing(m)}>View</Button><Button data-testid={`material-credit-${m.id}`} size="sm" variant="ghost" className="h-8 text-xs" onClick={() => openCn(m)}>Credit</Button><button data-testid={`material-del-${m.id}`} onClick={() => delMat(m)} className="ml-1 text-rose-400 hover:text-rose-600 align-middle"><Trash2 className="h-4 w-4" /></button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>}

      <CostDetailDialog m={viewing} onClose={() => setViewing(null)} incVat={incVat} />

      {showCredits && (typeFilter === "credit_note" || credits.length > 0) && (
        <div className="mt-6">
          <h3 className="text-xs font-bold uppercase tracking-wider text-zinc-500 mb-2 flex items-center gap-1.5"><ReceiptText className="h-3.5 w-3.5" /> Credit notes</h3>
          <div className="bg-white border border-zinc-200 rounded-md overflow-x-auto kqm-scroll">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs font-semibold uppercase tracking-wider text-zinc-500 border-b border-zinc-200">
                <th className="p-3">Date</th><th className="p-3">Site</th><th className="p-3">Supplier</th><th className="p-3">Customer</th><th className="p-3">Reason</th><th className="p-3">Linked to</th><th className="p-3 text-right">Supplier credit (↓cost)</th><th className="p-3 text-right">Customer credit (↓revenue)</th><th className="p-3"></th>
              </tr></thead>
              <tbody>
                {(showCredits ? shownCredits : []).length === 0 && <tr><td colSpan={9} className="p-6 text-center text-sm text-zinc-400" data-testid="credits-empty">No credit notes for this filter.</td></tr>}
                {shownCredits.map((c) => (
                  <tr key={c.id} data-testid={`credit-row-${c.id}`} className="border-b border-zinc-50 hover:bg-zinc-50">
                    <td className="p-3 num">{fmtDate(c.date)}</td>
                    <td className="p-3">{c.site_name}</td>
                    <td className="p-3 text-zinc-500">{c.supplier || "—"}</td>
                    <td className="p-3 text-zinc-500">{c.customer_name || "—"}</td>
                    <td className="p-3">{c.reason}</td>
                    <td className="p-3 text-zinc-500">{c.material_desc || "—"}</td>
                    <td className="p-3 num text-right text-emerald-600">{c.supplier_credit ? eur(c.supplier_credit) : "—"}</td>
                    <td className="p-3 num text-right text-rose-600">{c.customer_credit ? eur(c.customer_credit) : "—"}</td>
                    <td className="p-3 text-right"><button data-testid={`credit-del-${c.id}`} onClick={() => delCn(c.id)} className="text-rose-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Add cost dialog */}
      <Dialog open={create} onOpenChange={setCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Add cost</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Type</Label>
              <div className="grid grid-cols-2 gap-2 mt-1">
                {[["materials", "Materials", "Stock, cable, fittings"], ["supplier_costs", "Supplier Costs", "Plant hire, skips, subcontract labour"]].map(([v, l, d]) => (
                  <button key={v} type="button" data-testid={`m-type-${v}`} onClick={() => setForm({ ...form, cost_type: v })}
                    className={`text-left rounded-md border px-3 py-2 transition-colors ${form.cost_type === v ? "border-amber-500 bg-amber-50" : "border-zinc-200 hover:bg-zinc-50"}`}>
                    <div className="text-sm font-semibold">{l}</div><div className="text-[11px] text-zinc-500">{d}</div>
                  </button>
                ))}
              </div>
            </div>
            <div className="col-span-2">
              <Label>Site</Label>
              <Select value={form.site_id} onValueChange={(v) => setForm({ ...form, site_id: v })}>
                <SelectTrigger data-testid="m-site" className="mt-1"><SelectValue placeholder="Select site" /></SelectTrigger>
                <SelectContent>{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="m-cat" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{cats.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Supplier</Label>
              <Select value={form.supplierMode} onValueChange={(v) => setForm({ ...form, supplierMode: v, supplier: v === OTHER ? "" : v })}>
                <SelectTrigger data-testid="m-supplier" className="mt-1"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                <SelectContent>
                  {suppliers.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  <SelectItem value={OTHER}>Other… (add new)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.supplierMode === OTHER && (
              <div className="col-span-2"><Label>Add a new supplier</Label><Input data-testid="m-supplier-other" value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} placeholder="New supplier name" className="mt-1" /></div>
            )}
            <div className="col-span-2"><Label>Description</Label><Input data-testid="m-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1" /></div>
            <div><Label>Quantity</Label><Input data-testid="m-qty" type="number" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="mt-1" /></div>
            <div><Label>Unit cost € (ex VAT)</Label><Input data-testid="m-unit" type="number" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} className="mt-1" /></div>
            <div>
              <Label>VAT rate</Label>
              <Select value={String(form.vat_rate)} onValueChange={(v) => setForm({ ...form, vat_rate: Number(v) })}>
                <SelectTrigger data-testid="m-vat" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{[23, 13.5, 0].map((r) => <SelectItem key={r} value={String(r)}>{r}%</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="text-xs text-zinc-600 self-end pb-2" data-testid="m-vat-preview">Ex VAT {eur(Number(form.quantity) * Number(form.unit_cost) || 0)} · Inc VAT <b>{eur((Number(form.quantity) * Number(form.unit_cost) || 0) * (1 + Number(form.vat_rate) / 100))}</b></div>
            <div>
              <Label>Billing</Label>
              <Select value={form.billing} onValueChange={(v) => setForm({ ...form, billing: v })}>
                <SelectTrigger data-testid="m-billing" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="non_billable">Non-billable</SelectItem>
                  <SelectItem value="at_cost">Billable at cost</SelectItem>
                  <SelectItem value="markup">Billable with mark-up</SelectItem>
                  <SelectItem value="manual">Manual sale price</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.billing === "markup" && <div><Label>Mark-up %</Label><Input data-testid="m-markup" type="number" value={form.markup_pct} onChange={(e) => setForm({ ...form, markup_pct: e.target.value })} className="mt-1" /></div>}
            {form.billing === "manual" && <div><Label>Sale price €</Label><Input data-testid="m-sale" type="number" value={form.sale_price || ""} onChange={(e) => setForm({ ...form, sale_price: e.target.value })} className="mt-1" /></div>}
          </div>
          <Button data-testid="m-submit" onClick={submit} className="w-full bg-slate-900 text-white mt-2">Add cost</Button>
        </DialogContent>
      </Dialog>

      {/* Credit note dialog */}
      <Dialog open={cnOpen} onOpenChange={setCnOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Raise a credit note</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-500 -mt-1">Use either or both sides. A supplier credit reduces the job's material cost; a customer credit reduces the job's revenue.</p>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div className="col-span-2">
              <Label>Site / job</Label>
              <Select value={cn.site_id} onValueChange={(v) => setCn({ ...cn, site_id: v, material_id: "", customer_id: siteCustomer(v) })}>
                <SelectTrigger data-testid="cn-site" className="mt-1"><SelectValue placeholder="Select site" /></SelectTrigger>
                <SelectContent>{sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Label>Link to original entry (optional)</Label>
              <Select value={cn.material_id || NONE} onValueChange={(v) => setCn({ ...cn, material_id: v === NONE ? "" : v })}>
                <SelectTrigger data-testid="cn-material" className="mt-1"><SelectValue placeholder="None" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>None</SelectItem>
                  {cnSiteMaterials.map((m) => <SelectItem key={m.id} value={m.id}>{m.description || m.category} · {eur(m.total_cost)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-2 border-t border-zinc-100 pt-3 -mb-1 text-xs font-bold uppercase tracking-wider text-emerald-700">Supplier side (↓ cost)</div>
            <div>
              <Label>Supplier</Label>
              <Select value={cn.supplierMode} onValueChange={(v) => setCn({ ...cn, supplierMode: v, supplier: v === OTHER ? "" : v })}>
                <SelectTrigger data-testid="cn-supplier" className="mt-1"><SelectValue placeholder="Select supplier" /></SelectTrigger>
                <SelectContent>
                  {suppliers.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  <SelectItem value={OTHER}>Other… (add new)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Supplier credit €</Label><Input data-testid="cn-supplier-credit" type="number" value={cn.supplier_credit} onChange={(e) => setCn({ ...cn, supplier_credit: e.target.value })} placeholder="0.00" className="mt-1" /></div>
            {cn.supplierMode === OTHER && <div className="col-span-2"><Label>Add a new supplier</Label><Input data-testid="cn-supplier-other" value={cn.supplier} onChange={(e) => setCn({ ...cn, supplier: e.target.value })} placeholder="New supplier name" className="mt-1" /></div>}

            <div className="col-span-2 border-t border-zinc-100 pt-3 -mb-1 text-xs font-bold uppercase tracking-wider text-rose-700">Customer side (↓ revenue)</div>
            <div>
              <Label>Customer</Label>
              <Select value={cn.customer_id || NONE} onValueChange={(v) => setCn({ ...cn, customer_id: v === NONE ? "" : v })}>
                <SelectTrigger data-testid="cn-customer" className="mt-1"><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>None</SelectItem>
                  {customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Customer credit €</Label><Input data-testid="cn-customer-credit" type="number" value={cn.customer_credit} onChange={(e) => setCn({ ...cn, customer_credit: e.target.value })} placeholder="0.00" className="mt-1" /></div>

            <div className="col-span-2"><Label>Reason</Label><Input data-testid="cn-reason" value={cn.reason} onChange={(e) => setCn({ ...cn, reason: e.target.value })} placeholder="e.g. Returned faulty cable" className="mt-1" /></div>
          </div>
          <Button data-testid="cn-submit" onClick={submitCn} className="w-full bg-slate-900 text-white mt-2">Raise credit note</Button>
        </DialogContent>
      </Dialog>
      {/* Supplier spend dialog */}
      <Dialog open={spendOpen} onOpenChange={setSpendOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Supplier spend — all jobs</DialogTitle></DialogHeader>
          <p className="text-sm text-zinc-500 -mt-1">Total material &amp; cost spend per supplier across every site, net of supplier credit notes.</p>
          <div className="max-h-[60vh] overflow-y-auto kqm-scroll mt-2">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs font-semibold uppercase tracking-wider text-zinc-500 border-b border-zinc-200 sticky top-0 bg-white">
                <th className="p-2">Supplier</th><th className="p-2 text-right">Entries</th><th className="p-2 text-right">Sites</th><th className="p-2 text-right">Gross</th><th className="p-2 text-right">Credits</th><th className="p-2 text-right">Net spend</th>
              </tr></thead>
              <tbody>
                {spend.length === 0 && <tr><td colSpan={6} data-testid="spend-empty" className="p-6 text-center text-zinc-400">No supplier spend recorded yet.</td></tr>}
                {spend.map((s, i) => (
                  <tr key={s.supplier} data-testid={`spend-row-${i}`} className="border-b border-zinc-50 hover:bg-zinc-50">
                    <td className="p-2 font-medium text-slate-900">{s.supplier}</td>
                    <td className="p-2 num text-right text-zinc-500">{s.entries}</td>
                    <td className="p-2 num text-right text-zinc-500">{s.sites}</td>
                    <td className="p-2 num text-right">{eur(s.gross)}</td>
                    <td className="p-2 num text-right text-emerald-600">{s.credit ? eur(s.credit) : "—"}</td>
                    <td className="p-2 num text-right font-bold">{eur(s.net)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
